import { Skeleton } from '@/_components/ui/skeleton'
import { TableBody, TableHead, TableHeader, TableRow } from '@/_components/ui/table'

import { ContentContainer } from '@/features/_components/content-container'
import type { TExposedApplicationDTO } from '@doutorsim/shared/applications'
import type { TExposedPersonDeclarationWithMatchDTO } from '@doutorsim/shared/exposed/person-declarations'
import type { TExposedPersonOfficialDataDTO } from '@doutorsim/shared/exposed/persons'
import { useState } from 'react'
import { buildCreateAddress } from '../_helpers/build-create-address'
import { FIELD_CONFIG, UNIQUE_QUESTION_COUNT } from '../_helpers/field-config'
import { officialValueForRow, patientValueForRow } from '../_helpers/field-values'
import { isBirthDateValid } from '../_helpers/formatting'
import { useConfirmDeclarations } from '../_hooks/use-confirm-declarations'
import { useConfirmationRowState } from '../_hooks/use-confirmation-row-state'
import { ConfirmationActions } from './confirmation-actions'
import { ConfirmationHeader } from './confirmation-header'
import { DataConfirmationRow } from './data-confirmation-row'
import { RejectDialog } from './reject-dialog'

interface DataConfirmationContentProps {
  declarations: TExposedPersonDeclarationWithMatchDTO[]
  officialData: TExposedPersonOfficialDataDTO | null
  isLoading: boolean
  application: TExposedApplicationDTO
}

export const DataConfirmationContent = ({
  declarations,
  officialData,
  isLoading,
  application,
}: DataConfirmationContentProps) => {
  const { rows, declarationsByQuestion, toggle, setValue, allChecked, checkedCount } =
    useConfirmationRowState({ declarations })

  const { confirm, reject, isPending, isRejecting } = useConfirmDeclarations({ application })

  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)

  // Only the 4 root questions Robbu sends count toward "all answered" — the
  // address-* splits are server-derived and their count varies with ViaCEP success.
  const pendingDeclarations = declarations.filter(
    (d) => d.status === 'pending' && !d.question.startsWith('address-'),
  )

  const birthDateValue = rows.birth_date?.value ?? ''
  const birthDateInvalid = !isBirthDateValid(birthDateValue)

  const handleConfirm = () =>
    confirm({
      birthDate: rows.birth_date?.value ?? '',
      address: buildCreateAddress(rows),
    })

  const handleReject = async (reason: string) => {
    await reject(reason)
    setIsRejectDialogOpen(false)
  }

  if (isLoading) {
    return (
      <ContentContainer className="gap-4">
        <Skeleton className="h-5 w-52" />
        <Skeleton className="h-4 w-72" />
        <div className="mt-2 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-11 w-full" />
          ))}
        </div>
      </ContentContainer>
    )
  }

  return (
    <div className="flex flex-col gap-5 font-mono text-brand-grayscale-11">
      <div className="flex flex-col gap-5">
        <ConfirmationHeader checkedCount={checkedCount} />

        <div className="overflow-hidden rounded-lg border border-brand-grayscale-4 bg-white">
          <table className="w-full border-collapse font-mono text-sm">
            <colgroup>
              <col className="w-[160px]" />
              <col className="min-w-[160px]" />
              <col className="min-w-[160px]" />
              <col className="w-[32%] min-w-[240px] bg-brand-grayscale-1/60" />
              <col className="w-[160px]" />
            </colgroup>
            <TableHeader>
              <TableRow className="border-b border-brand-grayscale-4 bg-brand-grayscale-2 text-xs uppercase tracking-[0.14em]">
                <TableHead className="h-auto px-4 py-2.5 text-left font-semibold text-brand-grayscale-7">
                  Campo
                </TableHead>
                <TableHead className="h-auto border-l border-brand-grayscale-4 px-4 py-2.5 text-left font-semibold text-brand-grayscale-7">
                  Dados oficiais
                </TableHead>
                <TableHead className="h-auto border-l border-brand-grayscale-4 px-4 py-2.5 text-left font-semibold text-brand-grayscale-7">
                  Dados informados
                </TableHead>
                <TableHead className="h-auto border-l border-brand-grayscale-4 px-4 py-2.5 text-left font-bold text-brand-grayscale-10">
                  Dados confirmados
                </TableHead>
                <TableHead className="h-auto px-4 py-2.5 text-right font-bold text-brand-grayscale-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="[&_tr:last-child]:border-b [&_tr:last-child]:border-l-2">
              {FIELD_CONFIG.map((row) => {
                const declaration = declarationsByQuestion.get(row.question)
                const patientValue = patientValueForRow(row, declaration) ?? ''
                const officialValue = officialValueForRow(row, officialData)
                const state = rows[row.key]

                // Only flag a fully typed birth date that's still invalid — avoids
                // an error flashing while the user is mid-entry.
                const error =
                  row.key === 'birth_date' &&
                  (state?.value.length ?? 0) === 10 &&
                  !isBirthDateValid(state?.value ?? '')
                    ? 'Data de nascimento inválida'
                    : undefined

                return (
                  <DataConfirmationRow
                    key={row.key}
                    row={row}
                    patientValue={patientValue}
                    officialValue={officialValue}
                    match={declaration?.match}
                    incomeDelta={declaration?.incomeDelta ?? null}
                    editedValue={state?.value ?? patientValue}
                    isChecked={state?.checked ?? false}
                    error={error}
                    onValueChange={(value) => setValue(row.key, value)}
                    onToggle={() => toggle(row.key)}
                  />
                )
              })}
            </TableBody>
          </table>
        </div>
      </div>

      <ConfirmationActions
        canConfirm={
          allChecked && pendingDeclarations.length >= UNIQUE_QUESTION_COUNT && !birthDateInvalid
        }
        isPending={isPending}
        onReject={() => setIsRejectDialogOpen(true)}
        onConfirm={handleConfirm}
      />

      <RejectDialog
        open={isRejectDialogOpen}
        onOpenChange={setIsRejectDialogOpen}
        onConfirmReject={handleReject}
        isLoading={isRejecting}
      />
    </div>
  )
}
